<template>
  <transition name="overlay">
    <div
      v-if="isOpen"
      class="drawer-overlay"
      @click.self="closeDrawer"
    >
      <transition name="drawer">
        <div class="drawer-box">

          <!-- HEADER -->

          <div class="drawer-header">

            <div class="header-copy">

              <div class="header-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M12 3.8L14.3 8.45L19.45 9.2L15.72 12.83L16.6 17.95L12 15.53L7.4 17.95L8.28 12.83L4.55 9.2L9.7 8.45L12 3.8Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>

              <div>
                <h2 class="title">ч
                  Оцените компанию
                </h2>

                <p class="drawer-subtitle">
                  Ваш отзыв поможет другим пользователям
                </p>
              </div>

            </div>


            <button
              type="button"
              class="close-btn"
              aria-label="Закрыть"
              @click="closeDrawer"
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M5.5 5.5L14.5 14.5"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />

                <path
                  d="M14.5 5.5L5.5 14.5"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />
              </svg>
            </button>

          </div>


          <!-- RATINGS -->

          <div class="rating-section">

            <div
              v-for="(label, key) in ratingFields"
              :key="key"
              class="rating-field"
            >
              <div class="rating-copy">

                <span class="rating-label">
                  {{ label }}
                </span>

                <span
                  class="rating-value"
                  :class="{ active: ratings[key] > 0 }"
                >
                  {{ ratings[key] || "—" }}
                </span>

              </div>


              <div class="stars">

                <button
                  v-for="n in 5"
                  :key="n"
                  type="button"
                  class="star-button"
                  :class="{ active: ratings[key] >= n }"
                  @click="ratings[key] = n"
                >
                  <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 3.8L14.3 8.45L19.45 9.2L15.72 12.83L16.6 17.95L12 15.53L7.4 17.95L8.28 12.83L4.55 9.2L9.7 8.45L12 3.8Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>

              </div>

            </div>

          </div>


          <!-- COMMENT -->

          <div class="comment-section">

            <div class="field-header">
              <label class="label">
                Комментарий
              </label>

              <span class="optional">
                необязательно
              </span>
            </div>


            <div class="comment-wrap">

              <textarea
                v-model="comment"
                class="comment-input"
                placeholder="Расскажите о своём опыте работы с компанией..."
              ></textarea>

            </div>

          </div>


          <!-- ANONYMOUS -->

          <label class="anonymous-row">

            <div class="anonymous-copy">

              <div class="anonymous-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="8"
                    r="3"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />

                  <path
                    d="M6.5 18C7.2 14.8 9 13.3 12 13.3C15 13.3 16.8 14.8 17.5 18"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />

                  <path
                    d="M4 4L20 20"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                </svg>
              </div>


              <div>
                <div class="anonymous-title">
                  Анонимный отзыв
                </div>

                <div class="anonymous-description">
                  Имя и профиль не будут показаны
                </div>
              </div>

            </div>


            <div class="switch">

              <input
                id="anon"
                v-model="isAnonymous"
                type="checkbox"
              />

              <span class="switch-track">
                <span class="switch-thumb"></span>
              </span>

            </div>

          </label>


          <!-- FOOTER -->

          <div class="drawer-footer">

            <button
              type="button"
              class="cancel-btn"
              :disabled="isSubmitting"
              @click="closeDrawer"
            >
              Отмена
            </button>


            <button
              type="button"
              class="submit-btn"
              :disabled="isSubmitting"
              @click="submitReview"
            >

              <template v-if="!isSubmitting">

                <span>
                  Отправить отзыв
                </span>

                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M4 10H15.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />

                  <path
                    d="M11.5 6L15.5 10L11.5 14"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

              </template>


              <span
                v-else
                class="loading-content"
              >
                <span class="spinner"></span>

                Отправка
              </span>

            </button>

          </div>

        </div>
      </transition>
    </div>
  </transition>
</template>


<script setup>

import {
  ref
} from "vue";

import RatingService
  from "../api/RatingService";


const props =
  defineProps({

    isOpen: Boolean,

    companyId: String,

    raters: Array

  });


const emit =
  defineEmits([
    "close",
    "submitted"
  ]);


const ratings =
  ref({

    culture: 0,
    benefits: 0,
    management: 0,
    product: 0,
    balance: 0

  });


const ratingFields = {

  culture:
    "Культура компании",

  benefits:
    "Компенсейшн бенефитс",

  management:
    "Менеджмент",

  product:
    "Продукт / сервис",

  balance:
    "Ворк / лайф баланс"

};


const comment =
  ref("");


const isSubmitting =
  ref(false);


const isAnonymous =
  ref(false);


const closeDrawer = () => {

  if (!isSubmitting.value) {
    emit("close");
  }

};


const submitReview =
  async () => {

    if (isSubmitting.value) {
      return;
    }


    const userId =
      localStorage.getItem(
        "user_id"
      );


    if (
      props.raters?.includes(
        userId
      )
    ) {

      const confirmEdit =
        confirm(
          "Вы уже оставляли отзыв. Хотите заменить его?"
        );


      if (!confirmEdit) {
        return;
      }

    }


    if (!comment.value.trim()) {

      alert(
        "Пожалуйста, введите комментарий"
      );

      return;

    }


    const reviewPayload = {

      company_id:
        props.companyId,

      comment:
        comment.value.trim(),

      is_anonymous:
        isAnonymous.value,

      worklife_balance:
        ratings.value.balance,

      compensation_benefits:
        ratings.value.benefits,

      professional_skills:
        ratings.value.product,

      managment:
        ratings.value.management,

      company_culture:
        ratings.value.culture

    };


    isSubmitting.value =
      true;


    try {

      const res =
        await RatingService
          .sendReview(
            reviewPayload
          );


      console.log(
        "✅ Review отправлен:",
        res
      );


      emit(
        "submitted"
      );


      emit(
        "close"
      );

    } catch (error) {

      console.error(
        "❌ Ошибка отправки отзыва:",
        error
      );


      alert(
        "Ошибка при отправке отзыва"
      );

    } finally {

      isSubmitting.value =
        false;

    }

  };

</script>


<style scoped>

/* ============================================================
   OVERLAY
============================================================ */

.drawer-overlay {
  position: fixed;

  inset: 0;

  z-index: 9999;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding: 28px;

  background:
    rgba(8, 11, 16, 0.45);

  backdrop-filter:
    blur(8px);

  -webkit-backdrop-filter:
    blur(8px);
}


/* ============================================================
   MODAL
============================================================ */

.drawer-box {
  width: 100%;
  max-width: 590px;

  max-height:
    calc(100dvh - 56px);

  overflow-y: auto;

  box-sizing: border-box;

  padding: 26px;

  color:
    #2d3239;

  background:
    rgba(255, 255, 255, 0.985);

  border:
    1px solid
    rgba(35, 50, 70, 0.07);

  border-radius:
    24px;

  box-shadow:
    0
    28px
    80px
    rgba(0, 0, 0, 0.20);

  font-family:
    "Eastman-Regular",
    sans-serif;

  scrollbar-width:
    none;
}


.drawer-box::-webkit-scrollbar {
  display:
    none;
}


/* ============================================================
   HEADER
============================================================ */

.drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 18px;

  padding-bottom:
    20px;
}


.header-copy {
  display: flex;
  align-items: center;

  gap: 12px;
}


.header-icon {
  width: 42px;
  height: 42px;

  flex:
    0
    0
    42px;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #4b7fbd;

  background:
    rgba(70, 120, 190, 0.08);

  border:
    1px solid
    rgba(70, 120, 190, 0.08);

  border-radius:
    12px;
}


.header-icon svg {
  width: 20px;
  height: 20px;
}


.title {
  margin: 0;

  color:
    #292e35;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    18px;

  line-height:
    1.2;

  letter-spacing:
    -0.45px;
}


.drawer-subtitle {
  margin:
    4px
    0
    0;

  color:
    #8b9199;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;

  line-height:
    1.4;
}


/* ============================================================
   CLOSE
============================================================ */

.close-btn {
  width: 32px;
  height: 32px;

  flex:
    0
    0
    32px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  color:
    #78808a;

  background:
    #f3f5f7;

  border:
    1px solid
    rgba(40, 55, 75, 0.055);

  border-radius:
    10px;

  cursor:
    pointer;

  transition:
    color .18s ease,
    background-color .18s ease,
    transform .18s ease;
}


.close-btn svg {
  width: 15px;
  height: 15px;
}


.close-btn:hover {
  color:
    #414850;

  background:
    #eaedf1;

  transform:
    scale(1.04);
}


/* ============================================================
   RATINGS
============================================================ */

.rating-section {
  display: flex;
  flex-direction: column;

  gap: 8px;

  padding:
    10px;

  background:
    #f6f7f9;

  border:
    1px solid
    rgba(35, 50, 70, 0.055);

  border-radius:
    18px;
}


.rating-field {
  min-height: 47px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 18px;

  box-sizing: border-box;

  padding:
    8px
    10px;

  border-radius:
    12px;

  transition:
    background-color .18s ease;
}


.rating-field:hover {
  background:
    #ffffff;
}


.rating-copy {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 8px;
}


.rating-label {
  overflow: hidden;

  color:
    #555d67;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    11px;

  line-height:
    1.25;

  white-space:
    nowrap;

  text-overflow:
    ellipsis;
}


.rating-value {
  width: 22px;
  height: 22px;

  flex:
    0
    0
    22px;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #a1a7ae;

  background:
    rgba(35, 50, 70, 0.045);

  border-radius:
    7px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;

  transition:
    color .18s ease,
    background-color .18s ease;
}


.rating-value.active {
  color:
    #4f78aa;

  background:
    rgba(60, 115, 190, 0.09);
}


/* ============================================================
   STARS
============================================================ */

.stars {
  display: flex;
  align-items: center;

  gap: 3px;
}


.star-button {
  width: 25px;
  height: 25px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  color:
    #d7dbe1;

  background:
    transparent;

  border:
    none;

  cursor:
    pointer;

  transition:
    color .16s ease,
    transform .16s ease;
}


.star-button svg {
  width: 19px;
  height: 19px;
}


.star-button:hover {
  color:
    #8fb5e5;

  transform:
    translateY(-1px)
    scale(1.08);
}


.star-button.active {
  color:
    #5b94dd;
}


/* ============================================================
   COMMENT
============================================================ */

.comment-section {
  margin-top:
    20px;
}


.field-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom:
    8px;
}


.label {
  color:
    #555d67;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    11px;
}


.optional {
  color:
    #a0a5ad;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;
}


.comment-wrap {
  overflow: hidden;

  background:
    #f6f7f9;

  border:
    1px solid
    rgba(35, 50, 70, 0.07);

  border-radius:
    15px;

  transition:
    background-color .18s ease,
    border-color .18s ease,
    box-shadow .18s ease;
}


.comment-wrap:focus-within {
  background:
    #ffffff;

  border-color:
    rgba(55, 127, 228, 0.38);

  box-shadow:
    0
    0
    0
    4px
    rgba(55, 127, 228, 0.055);
}


.comment-input {
  width: 100%;
  height: 104px;

  display: block;

  box-sizing: border-box;

  padding:
    13px
    14px;

  resize: none;

  color:
    #363c44;

  background:
    transparent;

  border:
    none;

  outline:
    none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    12px;

  line-height:
    1.5;
}


.comment-input::placeholder {
  color:
    #a3a9b1;
}


/* ============================================================
   ANONYMOUS ROW
============================================================ */

.anonymous-row {
  margin-top:
    14px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 16px;

  box-sizing: border-box;

  padding:
    11px
    12px;

  background:
    #f8f9fa;

  border:
    1px solid
    rgba(35, 50, 70, 0.055);

  border-radius:
    14px;

  cursor:
    pointer;
}


.anonymous-copy {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 10px;
}


.anonymous-icon {
  width: 31px;
  height: 31px;

  flex:
    0
    0
    31px;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #77869a;

  background:
    #ffffff;

  border:
    1px solid
    rgba(40, 60, 85, 0.055);

  border-radius:
    9px;
}


.anonymous-icon svg {
  width: 16px;
  height: 16px;
}


.anonymous-title {
  color:
    #4c545e;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    10px;
}


.anonymous-description {
  margin-top:
    3px;

  color:
    #969ca4;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    8px;
}


/* ============================================================
   SWITCH
============================================================ */

.switch {
  position:
    relative;

  width:
    38px;

  height:
    22px;

  flex:
    0
    0
    38px;
}


.switch input {
  position:
    absolute;

  width:
    1px;

  height:
    1px;

  opacity:
    0;
}


.switch-track {
  position:
    absolute;

  inset:
    0;

  display:
    block;

  background:
    #d8dce1;

  border-radius:
    999px;

  transition:
    background-color .18s ease;
}


.switch-thumb {
  position:
    absolute;

  top:
    3px;

  left:
    3px;

  width:
    16px;

  height:
    16px;

  background:
    #ffffff;

  border-radius:
    50%;

  box-shadow:
    0
    2px
    5px
    rgba(0, 0, 0, 0.15);

  transition:
    transform .18s ease;
}


.switch
input:checked
+
.switch-track {
  background:
    #4f8ad5;
}


.switch
input:checked
+
.switch-track
.switch-thumb {
  transform:
    translateX(16px);
}


/* ============================================================
   FOOTER
============================================================ */

.drawer-footer {
  margin-top:
    20px;

  padding-top:
    18px;

  display: flex;
  justify-content: flex-end;

  gap: 9px;

  border-top:
    1px solid
    rgba(35, 50, 70, 0.06);
}


.cancel-btn,
.submit-btn {
  height:
    40px;

  box-sizing:
    border-box;

  padding:
    0
    17px;

  border-radius:
    11px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    11px;

  cursor:
    pointer;

  transition:
    transform .18s ease,
    background-color .18s ease,
    color .18s ease,
    border-color .18s ease,
    box-shadow .18s ease,
    opacity .18s ease;
}


.cancel-btn {
  color:
    #737b84;

  background:
    transparent;

  border:
    1px solid
    rgba(35, 50, 70, 0.08);
}


.cancel-btn:hover {
  color:
    #434a52;

  background:
    #f2f4f6;
}


.submit-btn {
  min-width:
    150px;

  display:
    flex;
  align-items:
    center;
  justify-content:
    center;

  gap:
    7px;

  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #3e84dc,
      #356aaa
    );

  border:
    1px solid
    rgba(50, 105, 180, 0.13);

  box-shadow:
    0
    8px
    18px
    rgba(40, 95, 170, 0.16);
}


.submit-btn svg {
  width:
    14px;

  height:
    14px;
}


.submit-btn:not(:disabled):hover {
  transform:
    translateY(-1px);

  box-shadow:
    0
    11px
    22px
    rgba(40, 95, 170, 0.22);
}


.submit-btn:disabled,
.cancel-btn:disabled {
  opacity:
    .48;

  cursor:
    default;

  pointer-events:
    none;
}


/* ============================================================
   LOADING
============================================================ */

.loading-content {
  display:
    inline-flex;

  align-items:
    center;

  gap:
    7px;
}


.spinner {
  width:
    13px;

  height:
    13px;

  box-sizing:
    border-box;

  border:
    2px solid
    rgba(255, 255, 255, .30);

  border-top-color:
    #ffffff;

  border-radius:
    50%;

  animation:
    spin
    .65s
    linear
    infinite;
}


@keyframes spin {

  to {
    transform:
      rotate(360deg);
  }

}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.drawer-overlay {
  background:
    rgba(0, 0, 0, .57);
}


html[data-theme="dark"]
.drawer-box {
  color:
    #eceef1;

  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(255, 255, 255, .065);

  box-shadow:
    0
    30px
    90px
    rgba(0, 0, 0, .46);
}


html[data-theme="dark"]
.header-icon {
  color:
    #91b6ee;

  background:
    rgba(90, 135, 195, .09);

  border-color:
    rgba(115, 155, 210, .08);
}


html[data-theme="dark"]
.title {
  color:
    #f0f1f3;
}


html[data-theme="dark"]
.drawer-subtitle {
  color:
    #858a93;
}


html[data-theme="dark"]
.close-btn {
  color:
    #9ea4ad;

  background:
    rgba(255, 255, 255, .04);

  border-color:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.close-btn:hover {
  color:
    #e3e5e8;

  background:
    rgba(255, 255, 255, .065);
}


html[data-theme="dark"]
.rating-section {
  background:
    rgba(255, 255, 255, .022);

  border-color:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.rating-field:hover {
  background:
    rgba(255, 255, 255, .028);
}


html[data-theme="dark"]
.rating-label {
  color:
    #c0c5cc;
}


html[data-theme="dark"]
.rating-value {
  color:
    #737a84;

  background:
    rgba(255, 255, 255, .035);
}


html[data-theme="dark"]
.rating-value.active {
  color:
    #a9c8f1;

  background:
    rgba(90, 140, 205, .09);
}


html[data-theme="dark"]
.star-button {
  color:
    #464a50;
}


html[data-theme="dark"]
.star-button:hover {
  color:
    #7faeea;
}


html[data-theme="dark"]
.star-button.active {
  color:
    #5b9eff;
}


html[data-theme="dark"]
.label {
  color:
    #c5c9cf;
}


html[data-theme="dark"]
.optional {
  color:
    #70757d;
}


html[data-theme="dark"]
.comment-wrap {
  background:
    rgba(255, 255, 255, .025);

  border-color:
    rgba(255, 255, 255, .065);
}


html[data-theme="dark"]
.comment-wrap:focus-within {
  background:
    rgba(255, 255, 255, .035);

  border-color:
    rgba(91, 158, 255, .34);

  box-shadow:
    0
    0
    0
    4px
    rgba(91, 158, 255, .05);
}


html[data-theme="dark"]
.comment-input {
  color:
    #dce0e5;
}


html[data-theme="dark"]
.comment-input::placeholder {
  color:
    #60666f;
}


html[data-theme="dark"]
.anonymous-row {
  background:
    rgba(255, 255, 255, .022);

  border-color:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.anonymous-icon {
  color:
    #91a3ba;

  background:
    rgba(255, 255, 255, .035);

  border-color:
    rgba(255, 255, 255, .05);
}


html[data-theme="dark"]
.anonymous-title {
  color:
    #cdd1d7;
}


html[data-theme="dark"]
.anonymous-description {
  color:
    #717780;
}


html[data-theme="dark"]
.switch-track {
  background:
    #3d4148;
}


html[data-theme="dark"]
.switch
input:checked
+
.switch-track {
  background:
    #4f82c5;
}


html[data-theme="dark"]
.drawer-footer {
  border-color:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.cancel-btn {
  color:
    #969ca5;

  border-color:
    rgba(255, 255, 255, .065);
}


html[data-theme="dark"]
.cancel-btn:hover {
  color:
    #d5d8dc;

  background:
    rgba(255, 255, 255, .04);
}


html[data-theme="dark"]
.submit-btn {
  background:
    linear-gradient(
      135deg,
      #3b6ea8,
      #304f78
    );

  border-color:
    rgba(255, 255, 255, .065);

  box-shadow:
    0
    9px
    22px
    rgba(0, 0, 0, .20);
}


/* ============================================================
   TRANSITIONS
============================================================ */

.overlay-enter-active,
.overlay-leave-active {
  transition:
    opacity
    .22s
    ease;
}


.overlay-enter-from,
.overlay-leave-to {
  opacity:
    0;
}


.drawer-enter-active,
.drawer-leave-active {
  transition:
    transform
    .28s
    cubic-bezier(.22, .61, .36, 1),
    opacity
    .22s
    ease;

  will-change:
    transform,
    opacity;
}


.drawer-enter-from,
.drawer-leave-to {
  transform:
    translateY(18px)
    scale(.985);

  opacity:
    0;
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 620px) {

  .drawer-overlay {
    align-items:
      flex-end;

    padding:
      0;
  }


  .drawer-box {
    max-width:
      none;

    max-height:
      92dvh;

    padding:
      20px
      16px
      calc(
        18px +
        env(safe-area-inset-bottom)
      );

    border-radius:
      22px
      22px
      0
      0;
  }


  .rating-field {
    align-items:
      flex-start;

    flex-direction:
      column;

    gap:
      7px;
  }


  .stars {
    width:
      100%;

    justify-content:
      space-between;
  }


  .star-button {
    width:
      34px;

    height:
      30px;
  }


  .star-button svg {
    width:
      21px;

    height:
      21px;
  }


  .cancel-btn {
    display:
      none;
  }


  .submit-btn {
    width:
      100%;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .overlay-enter-active,
  .overlay-leave-active,
  .drawer-enter-active,
  .drawer-leave-active,
  .close-btn,
  .star-button,
  .comment-wrap,
  .submit-btn,
  .cancel-btn,
  .switch-track,
  .switch-thumb {
    transition:
      none;
  }

}

</style>